import { useNavigate } from '@tanstack/react-router';
import { Sheet, SheetOpenFull } from '../ui/Sheet';
import { Button } from '../ui/Button';
import { StatusBadge } from '../ui/Badge';
import { VISIT_STATUS, JOB_STATUS, REQUEST_STATUS, display } from '../status';
import { timeInTz } from '../../lib/format';

/**
 * What the three panes hand the panel.
 *
 * ⭐ ONE shape, three producers — the board card, the map pin and a queue row (113/A5:
 * "one state, one query, one component"). Each pane maps its own row into this; the panel
 * itself fetches nothing, so opening a preview costs no round trip and cannot show a
 * spinner over data the page is already holding.
 *
 * ⚠️ `visitId` is nullable and that is the interesting case, not an edge. The unscheduled
 * queue is made ENTIRELY of jobs with no visit — that is what "unscheduled" means — and a
 * request has neither a visit nor a job yet. A panel that assumed a visit would be a panel
 * two of the four queues could not open.
 */
export interface PreviewTarget {
  /** What the page's selection is keyed on: the visit id when there is one, else the row's. */
  key: string;
  jobId: string | null;
  visitId: string | null;
  /** The kind decides which status vocabulary reads it, and where "open" goes. */
  kind: 'visit' | 'job' | 'request';
  /** "#1042" or "R-17". */
  ref: string;
  title: string;
  status: string;
  customer: string;
  /**
   * `null` is "none on file", drawn as the dash. `undefined` is "this opener does not carry
   * it" (a queue row's projection, the crew map's allow-list), and the row is left out: a
   * dash there would say the shop has none. Required, so every opener says which.
   */
  phone: string | null | undefined;
  propertyLabel: string | null;
  /** As `phone`: `null` is none on file, `undefined` is not carried (the row is left out). */
  address: string | null | undefined;
  assignees: string[];
  startAt: Date | string | null;
  endAt: Date | string | null;
  /** Why this visit cannot be moved, in the server's own words — or null. */
  stuck: string | null;
}

const VOCAB = {
  visit: VISIT_STATUS,
  job: JOB_STATUS,
  request: REQUEST_STATUS,
} as const;

/**
 * The job preview — the panel all three panes of the dispatch workspace open (114 part 4).
 *
 * 🟢 **A REUSE, not a new panel.** `v2/ui/Sheet.tsx` shipped with ticket 104 built to
 * Sonar's POSidePanel anatomy — header, body, footer action — and has two dozen call sites.
 * Building a second one here would have been a second focus trap, a second Esc handler and
 * a second set of mobile rules to keep in step.
 *
 * ⚠️ At ≥1280 it OVERLAYS the map rather than taking a fourth column (A5). The board already
 * gives the map its column; a panel that pushed a third one would reflow the board every
 * time somebody clicked a card.
 */
export function JobPreviewPanel({
  target,
  tz,
  onClose,
  onMoveDays,
  moving,
}: {
  target: PreviewTarget | null;
  tz: string;
  onClose: () => void;
  /** Keyboard equivalent of the drag. Absent for a row with no visit to move. */
  onMoveDays: (visitId: string, days: number) => void;
  moving: boolean;
}) {
  const navigate = useNavigate();
  const s = target ? display(VOCAB[target.kind], target.status) : null;

  const openFull = () => {
    if (!target) return;
    if (target.jobId) {
      void navigate({ to: '/jobs/$jobId', params: { jobId: target.jobId } });
      return;
    }
    // A request has no detail route of its own — triage is the page that acts on it, and
    // sending somebody to a URL that does not exist is worse than sending them one level up.
    void navigate({ to: '/requests' });
  };

  return (
    <Sheet
      open={target !== null}
      onClose={onClose}
      title={target?.customer ?? ''}
      badge={s ? <StatusBadge tone={s.tone}>{s.label}</StatusBadge> : null}
      footer={
        target ? (
          <SheetOpenFull
            onClick={openFull}
            label={target.jobId ? 'Open job' : 'Open in triage'}
          />
        ) : null
      }
    >
      {target ? (
        <div className="space-y-4 text-sm">
          <dl className="space-y-2">
            <Row k={target.kind === 'request' ? 'Request' : 'Job'}>
              {target.ref} · {target.title}
            </Row>
            <Row k="When">
              {target.startAt
                ? `${timeInTz(target.startAt, tz)}${
                    target.endAt ? `–${timeInTz(target.endAt, tz)}` : ''
                  }`
                : /* A job in the unscheduled queue has no visit at all, and saying so is the
                     whole reason it is in that queue. An em dash would read as missing data. */
                  'Not scheduled yet'}
            </Row>
            {target.propertyLabel ? <Row k="Property">{target.propertyLabel}</Row> : null}
            {target.address === undefined ? null : (
              <Row k="Address">{target.address ?? '—'}</Row>
            )}
            {target.phone === undefined ? null : (
              <Row k="Phone">{target.phone ?? '—'}</Row>
            )}
            <Row k="Assigned">
              {target.assignees.length > 0 ? target.assignees.join(', ') : 'Nobody yet'}
            </Row>
          </dl>

          {target.visitId ? (
            /* Keyboard-operable equivalent of the drag: dispatch without a mouse. */
            <div className="space-y-2 border-t border-border pt-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Move visit
              </p>
              {target.stuck ? (
                <p className="text-sm text-muted-foreground">
                  This one can&apos;t be moved — {target.stuck}.
                </p>
              ) : (
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    disabled={moving}
                    onClick={() => onMoveDays(target.visitId!, -1)}
                  >
                    ← Day earlier
                  </Button>
                  <Button
                    variant="ghost"
                    disabled={moving}
                    onClick={() => onMoveDays(target.visitId!, 1)}
                  >
                    Day later →
                  </Button>
                </div>
              )}
            </div>
          ) : null}
        </div>
      ) : null}
    </Sheet>
  );
}

/**
 * One label/value line.
 *
 * ⚠️ `break-words` on the value, and `text-right` WITHOUT `truncate`. At 390px the panel is
 * ~292px wide and an address is the longest thing in it; O2's ruling was that the address
 * WRAPS and never truncates, because half a street name is not an address. The board card
 * is where truncation is allowed — the card is a hint, this is the answer.
 */
function Row({ k, children }: { k: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="shrink-0 text-muted-foreground">{k}</dt>
      <dd className="min-w-0 break-words text-right font-medium">{children}</dd>
    </div>
  );
}
